import { useEffect, useRef } from 'react'
// v6 dropped maplibre-gl's default export (the v5-era `import maplibregl
// from 'maplibre-gl'` namespace object no longer exists); `Map` is a named
// export that doubles as both the constructor and the instance type.
import {
  Map as MapLibreMap,
  Marker,
  setMaxParallelImageRequests,
  setWorkerUrl,
  type FilterSpecification,
  type GeoJSONSource,
} from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
// maplibre-gl resolves its worker at runtime from `new URL('./' + name,
// import.meta.url)`. That template string is invisible to the bundler, so
// Vite never emits the worker file, and after bundling `import.meta.url` is
// the app chunk — leaving the library asking for /assets/maplibre-gl-worker.mjs,
// which 404s in production. `?worker&url` makes Vite bundle the worker together
// with the `./maplibre-gl-shared.mjs` it imports (emitting the file alone would
// break on that relative import) and hand back a hashed URL, which the
// library's own setter then points it at.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'
import type { Area, AreaType, Line } from '../areas/useAreas'
import { ASF_BOUNDS, BOUNDS_PADDING_PX } from './bounds'
import { tileSizeOf, toMapLibreTileUrl, type TangerLayerSpec } from './tileUrl'

setWorkerUrl(maplibreWorkerUrl)

/** Halved from maplibre-gl's default of 16. The setting is global — it covers
 *  the base map's tiles as well as the heatmap's — because the library offers
 *  no per-source equivalent. Lowering it trades a slightly slower fill for
 *  shorter request bursts against a tileserver that admits a bounded number of
 *  concurrent requests and 503s the overflow. */
const MAX_PARALLEL_IMAGE_REQUESTS = 8
setMaxParallelImageRequests(MAX_PARALLEL_IMAGE_REQUESTS)

const BASEMAP_ID = 'basemap'
const HEATMAP_ID = 'heatmap'

/** Request heatmap tiles at 512px where the layer offers it: one tile then
 *  covers what four 256px tiles would, cutting a pan's request count to a
 *  quarter at the same on-screen pixel density. Safe here only because heatmap
 *  rendering is zoom-agnostic — the base map deliberately keeps its own
 *  offered width, since drawing it from a lower zoom level would show that
 *  level's cartography at the wrong scale. */
const HEATMAP_TILE_WIDTH = 512

const LINES_SOURCE_ID = 'lines'
const LINES_LAYER_ID = 'lines'
const LINE_WIDTH = 3

// Border colour and width are NOT constants here: they are configured per
// (group, area type) in configuration/asf-fin.heatmapBorderGroups and arrive on
// each area — see AreaLayerGroup and groupAreasByLayer below.

function rasterSource(spec: TangerLayerSpec, preferredWidth?: number) {
  return {
    type: 'raster' as const,
    // Both must agree: the width baked into the URL is the width MapLibre is
    // told each tile is, or it lays the tiles out at the wrong scale.
    tiles: [toMapLibreTileUrl(spec, preferredWidth)],
    tileSize: tileSizeOf(spec, preferredWidth),
    minzoom: spec.minZ,
    maxzoom: spec.maxZ,
  }
}

// Area borders are closed rings. The server sends each ring's vertices
// without repeating the first one (see AreaDto), so drawing them as a plain
// LineString would leave the border open across the start/end vertex — hence
// the explicit close here. `lines` are open paths, drawn as received, and
// must never go through this.
function closedRing(coordinates: [number, number][]): [number, number][] {
  if (coordinates.length === 0) return coordinates
  return [...coordinates, coordinates[0]]
}

// The average of a ring's vertices. Not a true (area-weighted) polygon
// centroid — that's more math than placing a label needs — but good enough
// to land inside these roughly-convex hunting-ground shapes.
function centroidOf(coordinates: [number, number][]): [number, number] {
  const sum = coordinates.reduce<[number, number]>(
    ([lonSum, latSum], [lon, lat]) => [lonSum + lon, latSum + lat],
    [0, 0],
  )
  return [sum[0] / coordinates.length, sum[1] / coordinates.length]
}

// A minimal FeatureCollection-of-LineStrings shape. Deliberately not typed as
// the ambient `GeoJSON.FeatureCollection` — this project's tsconfig `types`
// list doesn't expose that global namespace for our own files to name
// directly, only for maplibre-gl's own declarations to resolve internally —
// so this local shape is checked structurally against `addSource`'s and
// `GeoJSONSource.setData`'s `GeoJSON.GeoJSON | string` parameter instead.
interface LineFeatureCollection {
  type: 'FeatureCollection'
  features: {
    type: 'Feature'
    properties: Record<string, unknown>
    geometry: { type: 'LineString'; coordinates: [number, number][] }
  }[]
}

function areaFeatureCollection(areas: Area[]): LineFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: areas.map((area) => ({
      type: 'Feature',
      // `id` is read back by the subs layer's filter (see
      // subVisibilityFilter) to hide one sub area without a per-area layer.
      properties: { id: area.id },
      geometry: { type: 'LineString', coordinates: closedRing(area.coordinates) },
    })),
  }
}

function lineFeatureCollection(lines: Line[]): LineFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: lines.map((line) => ({
      type: 'Feature',
      properties: { color: line.color },
      geometry: { type: 'LineString', coordinates: line.coordinates },
    })),
  }
}

// A single filter expression on the ONE subs layer, rather than one layer per
// sub area — toggling an id in/out of `hiddenSubIds` only ever has to touch
// this one layer's filter, not a per-area layer set.
function subVisibilityFilter(hiddenSubIds: ReadonlySet<string>): FilterSpecification {
  return ['!', ['in', ['get', 'id'], ['literal', Array.from(hiddenSubIds)]]]
}

interface AreaLayerGroup {
  id: string
  areaType: AreaType
  color: string
  width: number
  areas: Area[]
}

// `/areas` returns areas already in drawing order (bottom to top), with every
// area of one (groupId, areaType) pair contiguous and sharing one
// colour/width. Grouping by the first appearance of that pair turns the flat
// array back into the layer stack the API already decided, rather than this
// component re-deriving an ordering rule of its own — the order this
// function returns groups in IS the order their layers get installed in.
function groupAreasByLayer(areas: Area[]): AreaLayerGroup[] {
  const order: string[] = []
  const groups = new Map<string, AreaLayerGroup>()
  for (const area of areas) {
    const id = `areas-${area.groupId}-${area.areaType}`
    let group = groups.get(id)
    if (!group) {
      group = { id, areaType: area.areaType, color: area.color, width: area.width, areas: [] }
      groups.set(id, group)
      order.push(id)
    }
    group.areas.push(area)
  }
  return order.map((id) => groups.get(id)!)
}

// The bottom-most border layer that already exists on the map, if any,
// falling back to `lines` when no border layer is installed yet. A
// (re-)added heatmap layer is placed directly below whatever this returns via
// `beforeId`: `addLayer` with no `beforeId` always appends to the TOP of the
// stack, which would draw a freshly swapped-in heatmap over borders (or
// lines) already on the map from an earlier render. `borderLayerIds` is the
// installed border layers in stacking order, bottom to top — see the
// `borderLayers` ref in MapView, whose Map iteration order doubles as that
// stacking order.
function borderAnchorLayerId(map: MapLibreMap, borderLayerIds: readonly string[]): string | undefined {
  return [...borderLayerIds, LINES_LAYER_ID].find((id) => map.getLayer(id))
}

// Adds a GeoJSON source + its layer the first time this border group's data
// arrives, then updates the existing source's data on every later change.
// Unlike the heatmap, borders have no flicker to guard against swapping in a
// fresh source/layer pair every time would just be needless churn.
function upsertGeoJsonLayer(
  map: MapLibreMap,
  sourceId: string,
  data: LineFeatureCollection,
  addLayer: () => void,
) {
  const existing = map.getSource<GeoJSONSource>(sourceId)
  if (existing) {
    existing.setData(data)
  } else {
    map.addSource(sourceId, { type: 'geojson', data })
    addLayer()
  }
}

/// One context's currently-renderable heatmap layer. The context id is
/// carried alongside the spec because it identifies the layer on the map (its
/// source/layer id) and is what tells two areas' generations apart.
export interface HeatmapLayerEntry {
  contextId: string
  layer: TangerLayerSpec
}

export function MapView({
  basemap,
  heatmaps,
  areas,
  lines,
  showBorders,
  hiddenSubIds,
  heatmapOpacity,
  subLabel,
}: {
  basemap: TangerLayerSpec
  /// One entry per heatmap context currently showing a layer, in display
  /// order. Several, because one deployment covers several ASF surveillance
  /// areas, each with its own Tanger context, its own poll clock and its own
  /// layer generations (see HeatmapContextPoller).
  heatmaps: HeatmapLayerEntry[]
  areas: Area[]
  lines: Line[]
  // Covers the hunt-area border and the drawn lines together. Per-sub-area
  // visibility is a SEPARATE control (hiddenSubIds, below) — this switch
  // deliberately does not touch the subs layer or its labels.
  showBorders: boolean
  hiddenSubIds: ReadonlySet<string>
  heatmapOpacity: number
  subLabel: (area: Area) => string
}) {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MapLibreMap | null>(null)
  // The id of the heatmap source currently on the map FOR EACH context. A new
  // layer gets a new id so the old tiles keep painting until the new ones are
  // ready; keyed by context so two areas' swaps never touch each other.
  const heatmapSourceIds = useRef(new Map<string, string>())
  // How many generations each context has installed — the id suffix. Per
  // context rather than one global counter so a layer id reads as "this
  // area's nth layer" on a live map.
  const heatmapGenerations = useRef(new Map<string, number>())
  // The spec currently installed per context. A poll for ONE area mints a new
  // `heatmaps` array, so without this the other areas' identical layers would
  // be re-added — and their tiles re-downloaded — every time a neighbour
  // polled.
  const installedHeatmaps = useRef(new Map<string, TangerLayerSpec>())
  // Set true the instant the mount effect's own 'load' handler fires, false
  // again once that same map is torn down. This answers "has the style
  // finished its initial load" directly from the one event that answers it —
  // unlike maplibre-gl's `isStyleLoaded()`, which is `Style.loaded()` and
  // reports false whenever ANY source's tiles are still in flight (including
  // the basemap's, right after 'load' fires). Re-querying that instead of
  // remembering the answer is what let the heatmap effect miss 'load'
  // entirely: `map.once('load', install)` never replays an event that
  // already fired, so if `isStyleLoaded()` said "not yet" at the moment the
  // heatmap arrived, `install` would wait forever for an event with no more
  // listeners left to notify.
  const styleLoaded = useRef(false)
  // Mirrors whether `mapRef.current` is still a live, non-torn-down map.
  // Distinct from the heatmap effect's own per-run `cancelled` flag (below):
  // this one is shared across every heatmap generation, so a later
  // generation's effect starting does not retroactively invalidate an
  // earlier generation's still-pending cleanup (see removePrevious).
  const mapAlive = useRef(false)
  // Sub-area label markers currently on the map, keyed by area id. Plain
  // maplibregl.Marker + a styled div, not a symbol layer — the style has no
  // `glyphs` endpoint, and adding one would mean hosting fonts just for this.
  const markers = useRef(new Map<string, Marker>())
  // Installed border layer ids, keyed to their AreaType, in the order their
  // layers were added — a plain Map iterates its keys in insertion order, so
  // this doubles as the stacking order `borderAnchorLayerId` searches (bottom
  // to top) and as the lookup the showBorders/hiddenSubIds effects use to
  // find which installed layers are theirs to touch.
  const borderLayers = useRef(new Map<string, AreaType>())

  // heatmapOpacity is read through this ref at heatmap-INSTALL time rather
  // than being a dependency of the heatmap effect below: that effect is
  // deliberately keyed on [heatmap] alone (see its own comment), and an
  // opacity-only prop change is handled by the dedicated effect further down
  // instead of re-running the whole swap dance.
  const heatmapOpacityRef = useRef(heatmapOpacity)
  heatmapOpacityRef.current = heatmapOpacity

  // showBorders/hiddenSubIds are likewise read through refs at border-INSTALL
  // time (the borders effect below is keyed on [areas, lines], not on
  // these), so a freshly installed layer starts in the CURRENT visibility
  // state regardless of whether areas/lines happened to arrive before or
  // after the latest showBorders/hiddenSubIds change. The dedicated
  // visibility effects further down apply a later change to an
  // already-installed layer.
  const showBordersRef = useRef(showBorders)
  showBordersRef.current = showBorders
  const hiddenSubIdsRef = useRef(hiddenSubIds)
  hiddenSubIdsRef.current = hiddenSubIds

  useEffect(() => {
    if (!container.current) return

    // A style load is network-bound, so it can resolve after this component
    // has already unmounted (a slow connection, or React 18 StrictMode's
    // deliberate mount→unmount→remount in dev). Without this flag the 'load'
    // callback below would run its addSource/addLayer/fitBounds calls against
    // a Map that map.remove() already tore down.
    let cancelled = false

    const map = new MapLibreMap({
      container: container.current,
      // The basemap starts empty and is added explicitly once loaded (below),
      // the same way the heatmap layer is added later — one uniform "add a
      // raster layer once the map is ready" path rather than two.
      style: { version: 8, sources: {}, layers: [] },
      attributionControl: { compact: true },
    })
    mapRef.current = map
    mapAlive.current = true

    map.on('load', () => {
      if (cancelled) return
      styleLoaded.current = true
      map.addSource(BASEMAP_ID, rasterSource(basemap))
      map.addLayer({ id: BASEMAP_ID, type: 'raster', source: BASEMAP_ID })
      map.fitBounds(ASF_BOUNDS, { padding: BOUNDS_PADDING_PX, animate: false })
    })

    return () => {
      cancelled = true
      mapAlive.current = false
      styleLoaded.current = false
      markers.current.forEach((marker) => marker.remove())
      markers.current.clear()
      map.remove()
      mapRef.current = null
      heatmapSourceIds.current.clear()
      heatmapGenerations.current.clear()
      installedHeatmaps.current.clear()
      borderLayers.current.clear()
    }
    // The basemap spec is resolved once before this component mounts and never
    // changes for the life of the page, so re-creating the map on it would only
    // throw away the user's pan and zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Same reasoning as the effect above: `install`/`removePrevious` run from
    // an 'idle' or 'load' callback that can fire after this effect's own
    // cleanup — a heatmap re-render or an unmount — has already run.
    let cancelled = false

    function install() {
      if (cancelled) return

      // Computed HERE rather than when the effect ran: `install` can be
      // deferred to the style's 'load' event, by which time another poll may
      // have superseded what was pending.
      const pending = heatmaps.filter(
        (entry) => installedHeatmaps.current.get(entry.contextId) !== entry.layer,
      )
      const dropped = [...heatmapSourceIds.current.keys()].filter(
        (contextId) => !heatmaps.some((entry) => entry.contextId === contextId),
      )

      for (const entry of pending) {
        const previous = heatmapSourceIds.current.get(entry.contextId)
        const generation = (heatmapGenerations.current.get(entry.contextId) ?? 0) + 1
        heatmapGenerations.current.set(entry.contextId, generation)
        const id = `${HEATMAP_ID}-${entry.contextId}-${generation}`

        map!.addSource(id, rasterSource(entry.layer, HEATMAP_TILE_WIDTH))
        map!.addLayer(
          {
            id,
            type: 'raster',
            source: id,
            paint: { 'raster-opacity': heatmapOpacityRef.current },
          },
          // Below every border layer already on the map (see
          // borderAnchorLayerId) — otherwise this addLayer call, which has no
          // other way to say "below", would append the fresh heatmap ABOVE
          // borders added by an earlier render. Two contexts' layers end up
          // stacked in insertion order among themselves, which is
          // immaterial: the surveillance areas are geographically disjoint,
          // so no pixel is covered by more than one of them.
          borderAnchorLayerId(map!, [...borderLayers.current.keys()]),
        )

        // Drop this context's previous layer only once the new one has
        // painted, so swapping in a fresher heatmap never flashes an empty
        // map.
        const removePrevious = () => {
          // Guarded on the MAP's liveness, not this effect run's own
          // `cancelled` flag. `cancelled` flips the moment THIS generation is
          // superseded by the next heatmap prop change — which happens on
          // every subsequent poll — so gating removal on it meant generation
          // n's cleanup cancelled the removal of generation n-1, and
          // generation n+1 only ever learned about n: n-1 leaked on the map
          // forever. The map instance outlives any single heatmap
          // generation; only a real unmount (mapAlive -> false) should block
          // a still-pending removal.
          if (!mapAlive.current || !previous) return
          if (map!.getLayer(previous)) map!.removeLayer(previous)
          if (map!.getSource(previous)) map!.removeSource(previous)
        }
        map!.once('idle', removePrevious)
        heatmapSourceIds.current.set(entry.contextId, id)
        installedHeatmaps.current.set(entry.contextId, entry.layer)
      }

      // A context that stops reporting a layer (removed from
      // configuration/asf-fin, or a reload that returned fewer areas) has its
      // layer taken off immediately rather than on the next 'idle': there is
      // no replacement painting over it, so waiting would leave stale tiles
      // for an area nobody asked about.
      for (const contextId of dropped) {
        const id = heatmapSourceIds.current.get(contextId)
        if (id !== undefined) {
          if (map!.getLayer(id)) map!.removeLayer(id)
          if (map!.getSource(id)) map!.removeSource(id)
        }
        heatmapSourceIds.current.delete(contextId)
        heatmapGenerations.current.delete(contextId)
        installedHeatmaps.current.delete(contextId)
      }
    }

    if (styleLoaded.current) install()
    else map.once('load', install)

    return () => {
      cancelled = true
    }
  }, [heatmaps])

  // Reacts to an opacity-only prop change on the CURRENTLY installed heatmap
  // layer. Deliberately separate from the effect above: heatmapOpacity can
  // change (a settings toggle) independently of the heatmap layer itself
  // swapping in from a poll, and re-running the whole install/swap dance for
  // an opacity change alone would needlessly re-add the same tiles.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    // Every context's layer: the slider is a rendering preference for the
    // heatmap as a whole, not a per-area setting.
    for (const id of heatmapSourceIds.current.values()) {
      if (map.getLayer(id)) map.setPaintProperty(id, 'raster-opacity', heatmapOpacity)
    }
  }, [heatmapOpacity])

  // Installs (or updates the data of) one line layer per (groupId, areaType)
  // pair, plus the `lines` layer. Same load-timing guard as the heatmap
  // effect above: this can run before the style has finished its initial
  // load.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    let cancelled = false

    function install() {
      if (cancelled) return

      const groups = groupAreasByLayer(areas)
      const currentIds = new Set(groups.map((group) => group.id))

      // A pair that no longer appears in `areas` — a group unassigned, or a
      // ground type no longer configured for it — is torn down: its source
      // goes with its layer, and nothing keeps referencing either id once
      // removed here.
      for (const id of [...borderLayers.current.keys()]) {
        if (currentIds.has(id)) continue
        if (map!.getLayer(id)) map!.removeLayer(id)
        if (map!.getSource(id)) map!.removeSource(id)
        borderLayers.current.delete(id)
      }

      for (const group of groups) {
        upsertGeoJsonLayer(map!, group.id, areaFeatureCollection(group.areas), () => {
          const visibility: 'visible' | 'none' = showBordersRef.current ? 'visible' : 'none'
          const spec = {
            id: group.id,
            type: 'line' as const,
            source: group.id,
            paint: { 'line-color': group.color, 'line-width': group.width },
            // `sub` visibility is per-area (hiddenSubIds' filter); `hunt` and
            // `forbidden` visibility is the single showBorders switch. Baked
            // in at install time from the ref, same as before — the dedicated
            // effects below apply a LATER change to an already-installed
            // layer.
            ...(group.areaType === 'sub'
              ? { filter: subVisibilityFilter(hiddenSubIdsRef.current) }
              : { layout: { visibility } }),
          }
          // A pair installed for the first time while other layers are already
          // on the map (a group or ground type that only starts existing after
          // initial load) must land at its CONFIGURED position, not on top: the
          // whole point of the ordered configuration is border-vs-border
          // stacking — one group's sub borders drawn over another group's where
          // they overlap — so it goes directly below the nearest pair that comes
          // after it and is already installed, or below `lines` when it is the
          // topmost pair. On the initial install nothing after it exists yet and
          // this degrades to a plain append, which is the same order.
          const nextInstalled = groups
            .slice(groups.indexOf(group) + 1)
            .map((later) => later.id)
            .find((id) => map!.getLayer(id))
          const beforeId = nextInstalled ?? (map!.getLayer(LINES_LAYER_ID) ? LINES_LAYER_ID : undefined)
          if (beforeId) map!.addLayer(spec, beforeId)
          else map!.addLayer(spec)
        })
      }

      // Rebuilt in the configured order rather than appended to, so the ref's
      // iteration order stays the stacking order even after a pair was slotted
      // into the middle of an existing stack above.
      borderLayers.current = new Map(groups.map((group) => [group.id, group.areaType]))

      upsertGeoJsonLayer(map!, LINES_SOURCE_ID, lineFeatureCollection(lines), () =>
        map!.addLayer({
          id: LINES_LAYER_ID,
          type: 'line',
          source: LINES_SOURCE_ID,
          layout: { visibility: showBordersRef.current ? 'visible' : 'none' },
          paint: { 'line-color': ['get', 'color'], 'line-width': LINE_WIDTH },
        }),
      )
    }

    if (styleLoaded.current) install()
    else map.once('load', install)

    return () => {
      cancelled = true
    }
  }, [areas, lines])

  // showBorders governs every hunt/forbidden border layer and the drawn
  // lines — never a sub layer (see the prop's own comment). It is read here
  // (not folded into the borders-install effect above) so toggling it
  // doesn't need areas/lines to have changed too.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const visibility = showBorders ? 'visible' : 'none'
    for (const [id, areaType] of borderLayers.current) {
      if (areaType === 'sub') continue
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visibility)
    }
    if (map.getLayer(LINES_LAYER_ID)) map.setLayoutProperty(LINES_LAYER_ID, 'visibility', visibility)
  }, [showBorders])

  // Per-sub-area visibility is one filter per sub layer, not one layer per
  // sub area (there are 16+ subs today) — and now potentially more than one
  // sub layer, one per group, so every installed sub layer gets the filter.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    for (const [id, areaType] of borderLayers.current) {
      if (areaType !== 'sub') continue
      if (map.getLayer(id)) map.setFilter(id, subVisibilityFilter(hiddenSubIds))
    }
  }, [hiddenSubIds])

  // Sub-area label markers. Governed by hiddenSubIds only, same as the subs
  // layer's filter above — NOT by showBorders, which stops at the hunt
  // border and lines (see the prop's own comment).
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const visibleSubs = areas.filter(
      (area) => area.areaType === 'sub' && !hiddenSubIds.has(area.id),
    )
    const visibleIds = new Set(visibleSubs.map((area) => area.id))

    // Drop markers that are no longer visible before adding/updating the
    // rest, rather than tearing every marker down and rebuilding it on each
    // render.
    for (const [id, marker] of markers.current) {
      if (!visibleIds.has(id)) {
        marker.remove()
        markers.current.delete(id)
      }
    }

    for (const area of visibleSubs) {
      const label = subLabel(area)
      const existing = markers.current.get(area.id)
      if (existing) {
        existing.getElement().textContent = label
      } else {
        const el = document.createElement('div')
        el.className = 'asf-sub-label'
        el.textContent = label
        markers.current.set(
          area.id,
          new Marker({ element: el }).setLngLat(centroidOf(area.coordinates)).addTo(map),
        )
      }
    }
  }, [areas, hiddenSubIds, subLabel])

  return <div ref={container} className="asf-map" data-testid="asf-map" />
}
